import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import ModalContext from 'contexts/ModalContext'
import JourneyV2DetailsModal from 'components/Flights/V2/FareFamily/JourneyV2DetailsModal'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import flightSearchWidgetStateReducer, {
  initialState,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetState, FlightSearchWidgetDispatchAction } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetDesktop from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetMobile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalProvider from 'contexts/ModalProvider'
import useSearchFlights from 'hooks/useSearchFlights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FlightSegment, FlightSortOptions, FlightsFareTypes, fareClassToUrlMap, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { resetFlightsTimer, hasFlightSessionExpired } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { FlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/FlightSessionCountdownClock'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getTotalStopCount } from 'lib/flights/flightUtils'
import type { TransportOption } from 'tripPlanner/api/types'
import { useAppSelector } from 'hooks/reduxHooks'
import { cacheShortlistedJourneys } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/shortlistedJourneyCache'
import ChatFlightResultsHeader from 'tripPlanner/components/Chat/Flights/ChatFlightResultsHeader'
import useChatFlightResultsSorting from 'tripPlanner/components/Chat/Flights/useChatFlightResultsSorting'
import DrawerFlightCard from './DrawerFlightCard'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightsLoadingTopBar from 'components/Flights/V2/FlightsLoadingTopBar'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import StepperProgressSteps from 'LuxKit/Steppers/StepperProgressSteps'
import StepperProgressStep from 'LuxKit/Steppers/StepperProgressStep'
import type { StepClickHandler } from 'LuxKit/Steppers/StepperProgressStep'
import uuidV4 from 'lib/string/uuidV4Utils'
import { useChatStreamStatus } from '../../contexts/ChatStreamStatusContext'

const SearchWrapper = styled.div`
  padding: ${rem(12)} ${rem(24)};
  max-width: ${rem(800)};
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`

const ContentWrapper = styled.div`
  padding: ${rem(24)};
  max-width: ${rem(800)};
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`

const CountdownWrapper = styled.div`
  padding: ${rem(8)} ${rem(24)};
  box-sizing: border-box;
`

const EMPTY_ITINERARY = [{ departureAirport: '', arrivalAirport: '', departureDate: '' }]

const EMPTY_SEARCH_PARAMS: FlightSearchParams = {
  itinerary: EMPTY_ITINERARY,
  occupancies: [{ adults: 1 }],
  fareClass: 'economy',
}

interface Props {
  isOpen: boolean
  onClose: () => void
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  fareType?: string
  fareClass?: string
  passengers?: number
  autoSearch?: boolean
  onSendMessage?: (message: string) => void
  existingFlights?: Array<TransportOption>
}

interface ContentProps {
  onSendMessage?: (message: string) => void
  onClose: () => void
  existingFlightFingerprints: Set<string>
  autoSearch?: boolean
}

function getFlightFingerprint(flightNumber: string | undefined, departureAirport: string | undefined, arrivalAirport: string | undefined): string {
  return `${flightNumber ?? ''}|${departureAirport ?? ''}|${arrivalAirport ?? ''}`
}

function isJourneyAlreadyAdded(journey: App.JourneyV2, fingerprints: Set<string>): boolean {
  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  return fingerprints.has(getFlightFingerprint(firstFlight.flightNumber, firstFlight.departureAirport, lastFlight.arrivalAirport))
}

function FlightSearchDrawerContent({ onSendMessage, onClose, existingFlightFingerprints, autoSearch }: ContentProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { isStreaming } = useChatStreamStatus()
  const showModal = useContext(ModalContext)
  const [searchParams, setSearchParams] = useState<FlightSearchParams | undefined>(undefined)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchVersion, setSearchVersion] = useState(0)

  const [activeSegmentKey, setActiveSegmentKey] = useState<FlightSegment>(FlightSegment.DEPARTING)
  const [flightSort, setFlightSort] = useState<FlightSortOptions>(FlightSortOptions.Best)
  const [selectedFlights, setSelectedFlights] = useState<Record<string, App.JourneyV2>>({})
  const [selectedFareFamilies, setSelectedFareFamilies] = useState<Record<string, App.FlightFareFamily>>({})

  const widgetState = useContext(FlightSearchWidgetStateContext)
  const { flights, occupants, fareType, fareCabin } = widgetState

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const segments = useMemo<Array<FlightSegment>>(() => isReturn ? [FlightSegment.DEPARTING, FlightSegment.RETURNING] : [FlightSegment.DEPARTING], [isReturn])
  const isReturningStep = activeSegmentKey === FlightSegment.RETURNING

  const departDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkinDate ? moment(flight.checkinDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const returnDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkoutDate ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const handleSearch = useCallback(() => {
    const flight = flights[0]
    if (!flight.departureAirport?.airportCode || !flight.arrivalAirport?.airportCode || !flight.checkinDate) {
      return
    }

    const itinerary = [{
      departureAirport: flight.departureAirport.airportCode,
      arrivalAirport: flight.arrivalAirport.airportCode,
      departureDate: moment(flight.checkinDate).format(ISO_DATE_FORMAT),
    }]

    if (fareType?.value === FlightsFareTypes.RETURN && flight.checkoutDate) {
      itinerary.push({
        departureAirport: flight.arrivalAirport.airportCode,
        arrivalAirport: flight.departureAirport.airportCode,
        departureDate: moment(flight.checkoutDate).format(ISO_DATE_FORMAT),
      })
    }

    const nextVersion = searchVersion + 1
    const params: FlightSearchParams = {
      itinerary,
      occupancies: [occupants],
      fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] ?? 'economy' : 'economy',
      fareType: fareType?.value,
      key: nextVersion,
    }

    setSearchVersion(nextVersion)
    setSearchParams(params)
    setHasSearched(true)
    setActiveSegmentKey(FlightSegment.DEPARTING)
    setSelectedFlights({})
    setSelectedFareFamilies({})
  }, [flights, occupants, fareType, fareCabin, searchVersion])

  const hasAutoSearched = useRef(false)
  useEffect(() => {
    if (autoSearch && !hasAutoSearched.current) {
      hasAutoSearched.current = true
      handleSearch()
    }
  }, [autoSearch, handleSearch])

  const { journeys, fetching, error, searchResultsTimestamp, key: searchKey } = useSearchFlights(
    searchParams ?? EMPTY_SEARCH_PARAMS,
    {
      disabled: !searchParams,
      segments,
      activeSegmentKey,
      selectedFlights,
      selectedFareFamilies,
    },
  )

  const cachedSearchId = useAppSelector(state => state.flights.searchV2Flights[searchKey]?.searchId)
  const cachedSessionId = useAppSelector(state => state.flights.searchV2Flights[searchKey]?.sessionId)

  const displayJourneys = useChatFlightResultsSorting({
    journeys,
    activeSegmentKey,
    sort: flightSort,
    departDate,
    returnDate,
    selectedFlights,
  })

  const expiryTimestamp = useMemo(() => {
    if (!searchResultsTimestamp) return 0
    return moment(searchResultsTimestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()
  }, [searchResultsTimestamp])

  useEffect(() => {
    if (searchResultsTimestamp) {
      resetFlightsTimer(expiryTimestamp)
    }
  }, [expiryTimestamp, searchResultsTimestamp])

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  useEffect(() => {
    if (!hasSearched || fetching || !expiryTimestamp) return

    if (hasFlightSessionExpired()) {
      handleSearchRef.current()
      return
    }

    const remaining = expiryTimestamp - Date.now()
    if (remaining <= 0) {
      handleSearchRef.current()
      return
    }

    const timeout = setTimeout(() => {
      handleSearchRef.current()
    }, remaining)

    return () => clearTimeout(timeout)
  }, [hasSearched, fetching, expiryTimestamp])

  const currentSegmentIndex = useMemo(() => {
    return segments.findIndex(s => s === activeSegmentKey)
  }, [segments, activeSegmentKey])

  const handleStepClick = useCallback<StepClickHandler>((stepIndex) => {
    setActiveSegmentKey(segments[stepIndex])
    const keysToRemove = segments.slice(stepIndex)
    setSelectedFlights(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
    setSelectedFareFamilies(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
  }, [segments])

  const getStepSublabel = useCallback((journey: App.JourneyV2) => {
    const flight = journey.flightGroup.flights[0]
    const date = dateFormatter(flight.departureDateTime, INTL_DAY_MONTH_NAME)
    return `${date} · ${flight.departingTime}`
  }, [dateFormatter])

  const buildFlightContext = useCallback((journey: App.JourneyV2) => {
    const flights = journey.flightGroup.flights
    const firstFlight = flights[0]
    const lastFlight = flights[flights.length - 1]
    const duration = journey.flightGroup.totalFlightDuration
    return [
      `Carrier: ${journey.carrierName}`,
      `Flight: ${flights.map(f => f.flightNumber).join(' / ')}`,
      `From: ${firstFlight.departureAirport} (${firstFlight.departureAirportName})`,
      `To: ${lastFlight.arrivalAirport} (${lastFlight.arrivalAirportName})`,
      `Departure: ${firstFlight.departureDateTime}`,
      `Arrival: ${lastFlight.arrivalDateTime}`,
      `Duration: ${duration.hours}h ${duration.minutes}m`,
      `Stops: ${getTotalStopCount(flights)}`,
      `Price: ${journey.currencyCode} ${journey.price.adult.totalFare}`,
      `Fare type: ${journey.fareType}`,
      `Booking key: ${journey.bookingInfo.boookingKey}`,
      `Search ID: ${journey.bookingInfo.providerSearchId}`,
      `Journey Key: ${journey.bookingInfo.journeyKey}`,
      `Journey ID: ${journey.id}`,
      `Carrier logo: ${journey.carrierBrandMark ?? journey.carrierLogo}`,
    ].join('\n')
  }, [])

  const handleSelectFlight = useCallback((data: { journey: App.JourneyV2 }) => {
    const currentSegmentIndex = segments.findIndex(key => key === activeSegmentKey)

    if (currentSegmentIndex < segments.length - 1) {
      setSelectedFlights(prev => ({ ...prev, [activeSegmentKey]: data.journey }))
      setActiveSegmentKey(segments[currentSegmentIndex + 1])
      return
    }

    if (!onSendMessage || isStreaming) return

    const departingJourney = selectedFlights.departing
    const returningJourney = isReturn ? data.journey : undefined

    const outbound = departingJourney ?? data.journey
    const outboundFlights = outbound.flightGroup.flights
    const from = outboundFlights[0].departureAirport
    const to = outboundFlights[outboundFlights.length - 1].arrivalAirport

    const journeysToCache = isReturn && departingJourney && returningJourney
      ? [departingJourney, returningJourney]
      : [data.journey]
    cacheShortlistedJourneys(journeysToCache, cachedSearchId, cachedSessionId)

    let userMessage: string
    let contextContent: string

    if (isReturn && departingJourney && returningJourney) {
      userMessage = intl.formatMessage(
        {
          id: 'ZE1Bw2jDpdHXdHmw312L',
          defaultMessage: 'Add return flight from {from} to {to}',
          description: 'User chat message shown when adding a return flight from the flight search drawer',
        },
        {
          from,
          to,
        },
      )
      contextContent = `Departing flight:\n${buildFlightContext(departingJourney)}\n\nReturning flight:\n${buildFlightContext(returningJourney)}`
    } else {
      userMessage = intl.formatMessage(
        {
          id: 'mtc-4uBZ7D3tkBZV8ueR',
          defaultMessage: 'Add flight from {from} to {to}',
          description: 'User chat message shown when adding a one-way flight from the flight search drawer',
        },
        {
          from,
          to,
        },
      )
      contextContent = buildFlightContext(data.journey)
    }

    const searchMeta = [
      `Search expiry: ${expiryTimestamp}`,
      `Search params: ${searchParams ? JSON.stringify(searchParams) : ''}`,
    ].join('\n')

    const message = `${userMessage}\n<Context label="Flight search result">${contextContent}\n\n${searchMeta}</Context>`
    onSendMessage(message)
    onClose()
  }, [intl, segments, activeSegmentKey, selectedFlights, isReturn, onSendMessage, isStreaming, buildFlightContext, onClose, expiryTimestamp, searchParams, cachedSearchId, cachedSessionId])

  const flightsHeading = intl.formatMessage({
    id: '0py3lHxthzacj0uaxcjK',
    defaultMessage: 'Flights',
    description: 'Heading for the flight search drawer in trip planner',
  })

  const noResultsMessage = intl.formatMessage({
    id: 'P51jHD0yS8A8bb49PJWq',
    defaultMessage: 'No flights found for your search. Try different dates or airports.',
    description: 'Message shown when no flight results are found in the trip planner drawer',
  })

  const departingFlightLabel = intl.formatMessage({
    id: 'qJfCIiKntFNv9yNLHAHJ',
    defaultMessage: 'Departing flight',
    description: 'Label for departing flight segment in trip planner drawer',
  })

  const returningFlightLabel = intl.formatMessage({
    id: '4p1QhxNAMtAdkUW9nReN',
    defaultMessage: 'Returning flight',
    description: 'Label for returning flight segment in trip planner drawer',
  })

  const showResults = hasSearched && !fetching && displayJourneys.length > 0
  const showNoResults = hasSearched && !fetching && !error && displayJourneys.length === 0
  const showError = hasSearched && !fetching && !!error

  return (
    <>
      <SearchWrapper>
        <VerticalSpacer gap={16}>
          <Heading variant="heading3">{flightsHeading}</Heading>

          <CSSBreakpoint min="tablet">
            <FlightSearchWidgetDesktop onSearchAirport={handleSearch} />
          </CSSBreakpoint>
          <CSSBreakpoint max="mobile">
            <VerticalSpacer gap={12} as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleSearch() }}>
              <FlightSearchWidgetMobile />
              <TextButton kind="primary" size="large" type="submit" fit="full-width">
                {intl.formatMessage({
                  id: 'WGRNTZqigCaFkyNGLUr0',
                  defaultMessage: 'Search',
                  description: 'Search button label to initiate flight search in the trip planner drawer',
                })}
              </TextButton>
            </VerticalSpacer>
          </CSSBreakpoint>
        </VerticalSpacer>
      </SearchWrapper>

      {hasSearched && searchResultsTimestamp && (
        <CountdownWrapper>
          <FlightSessionCountdownClock />
        </CountdownWrapper>
      )}

      {hasSearched && isReturn && searchParams && (
        <ContentWrapper>
          <StepperProgressSteps activeStepIndex={currentSegmentIndex}>
            {searchParams.itinerary.map((leg, index) => (
              <StepperProgressStep
                key={segments[index]}
                stepIndex={index}
                label={intl.formatMessage(
                  {
                    id: 'AEiFGqvrHSc9lXaMfYat',
                    defaultMessage: '{origin} — {destination}',
                    description: 'Flight route step label showing origin and destination airport codes in trip planner',
                  },
                  { origin: leg.departureAirport, destination: leg.arrivalAirport },
                )}
                onStepClick={selectedFlights[segments[index]] ? handleStepClick : undefined}
                sublabel={selectedFlights[segments[index]] ? getStepSublabel(selectedFlights[segments[index]]) : undefined}
              />
            ))}
          </StepperProgressSteps>
        </ContentWrapper>
      )}

      {fetching && <>
        <FlightsLoadingTopBar />
        <ContentWrapper>
          <LoadingAnimationBox variant="flights" />
        </ContentWrapper>
      </>}

      {showError && (
        <ContentWrapper>
          <BodyText variant="medium" colour="critical">
            {intl.formatMessage({
              id: 'W6e3tDO59GW7SKXKCf3P',
              defaultMessage: 'Something went wrong while searching for flights. Please try again.',
              description: 'Error message when flight search fails in the trip planner drawer',
            })}
          </BodyText>
        </ContentWrapper>
      )}

      {showNoResults && (
        <ContentWrapper>
          <BodyText variant="medium" colour="neutral-two">{noResultsMessage}</BodyText>
        </ContentWrapper>
      )}

      {showResults && (
        <ContentWrapper>
          <VerticalSpacer gap={12}>
            <ChatFlightResultsHeader
              label={isReturn ? (isReturningStep ? returningFlightLabel : departingFlightLabel) : departingFlightLabel}
              count={displayJourneys.length}
              sort={flightSort}
              originAirportCode={searchParams?.itinerary[currentSegmentIndex]?.departureAirport}
              destinationAirportCode={searchParams?.itinerary[currentSegmentIndex]?.arrivalAirport}
              onSortChange={setFlightSort}
            />

            {displayJourneys.map((journey) => (
              <DrawerFlightCard
                key={journey.id}
                journey={journey}
                onSelect={handleSelectFlight}
                onViewDetails={() => showModal(<JourneyV2DetailsModal journey={journey} />)}
                isAlreadyAdded={isJourneyAlreadyAdded(journey, existingFlightFingerprints)}
                alignColumns
              />
            ))}
          </VerticalSpacer>
        </ContentWrapper>
      )}
    </>
  )
}

function FlightSearchDrawer({
  isOpen,
  onClose,
  originAirportCode,
  originAirportName,
  destinationAirportCode,
  destinationAirportName,
  departureDate,
  returnDate,
  fareType: fareTypeProp,
  fareClass: fareClassProp,
  passengers,
  autoSearch,
  onSendMessage,
  existingFlights,
}: Props) {
  const { fareTypes, fareCabins } = useFlightFareLabels()

  const existingFlightFingerprints = useMemo(() => {
    const set = new Set<string>()
    if (!existingFlights) return set
    for (const option of existingFlights) {
      const data = option.savedItemData
      if (data?.source === 'le_flight' && data.flightNumber) {
        set.add(getFlightFingerprint(data.flightNumber, data.departureAirport, data.arrivalAirport))
      }
    }
    return set
  }, [existingFlights])

  const widgetInitialState = useMemo((): FlightSearchWidgetState => {
    const resolvedFareType = fareTypeProp
      ? fareTypes.find(ft => ft.value === fareTypeProp)
      : undefined
    const resolvedFareCabin = fareClassProp
      ? fareCabins.find(fc => fc.value === fareClassProp)
      : undefined

    const state: FlightSearchWidgetState = {
      ...initialState,
      fareType: resolvedFareType ?? fareTypes.find(ft => ft.value === FlightsFareTypes.ONE_WAY) ?? fareTypes[0],
      fareCabin: resolvedFareCabin ?? fareCabins[0],
    }

    if (passengers && passengers > 0) {
      state.occupants = {
        ...state.occupants,
        adults: passengers,
      }
    }

    if (originAirportCode) {
      state.flights = [{
        ...state.flights[0],
        id: uuidV4(),
        departureAirport: {
          airportCode: originAirportCode,
          airportName: originAirportName ?? originAirportCode,
        } as App.AirportLocation,
      }]
    }

    if (destinationAirportCode && state.flights[0]) {
      state.flights = [{
        ...state.flights[0],
        arrivalAirport: {
          airportCode: destinationAirportCode,
          airportName: destinationAirportName ?? destinationAirportCode,
        } as App.AirportLocation,
      }]
    }

    if (departureDate) {
      state.flights = [{
        ...state.flights[0],
        checkinDate: moment(departureDate),
      }]
    }

    if (returnDate) {
      state.flights = [{
        ...state.flights[0],
        checkoutDate: moment(returnDate),
      }]
    }

    return state
  }, [fareTypes, fareCabins, fareTypeProp, fareClassProp, passengers, originAirportCode, originAirportName, destinationAirportCode, destinationAirportName, departureDate, returnDate])

  const [widgetState, widgetDispatch] = useReducer<FlightSearchWidgetState, [FlightSearchWidgetDispatchAction]>(
    flightSearchWidgetStateReducer,
    widgetInitialState,
  )

  if (!isOpen) return null

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      direction="right-to-left"
    >
      <FlightSearchWidgetStateProvider state={widgetState} dispatch={widgetDispatch}>
        <ModalProvider>
          <FlightSearchDrawerContent onSendMessage={onSendMessage} onClose={onClose} existingFlightFingerprints={existingFlightFingerprints} autoSearch={autoSearch} />
        </ModalProvider>
      </FlightSearchWidgetStateProvider>
    </TripDrawer>
  )
}

export default FlightSearchDrawer
